import { useState, useEffect } from 'react'
import { type service, type PortalChildSummary } from 'core/types'
import { sendGet } from 'hooks/use-fetch'
import { Grid, Typography, Box, Button, CircularProgress } from '@mui/material'
import { LargeCard } from 'Components/Cards/Large'
import { CustomLabelWithToolTip } from 'Components/CustomLabel/Index'
import { SelectableRow } from 'Pages/CaregiverPortal/InterestForm/SelectableRow'
import { childName, childAge } from 'Pages/CaregiverPortal/PortalHelpers'
import { toast } from 'react-toastify'

export interface ServiceAndChildSelectionProps {
  isdID: number
  dataSelected: (service: service, childID: number) => void
}

export function ServiceAndChildSelection (props: ServiceAndChildSelectionProps): JSX.Element {
  const [loading, setLoading] = useState<boolean>(true)
  const [children, setChildren] = useState<PortalChildSummary[]>([])
  const [availableServices, setAvailableServices] = useState<service[]>([])
  const [selectedService, setSelectedService] = useState<service | null>(null)
  const [selectedChildID, setSelectedChildID] = useState<number | null>(null)

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { response, success } = await sendGet('/CaregiverPortal/GetRelatedMembers', {})
      if (success) {
        setChildren(response.children)
      }

      const { response: resp2 } = await sendGet('/CaregiverPortal/GetCurrentEnabledServicesForCaregiver', {})
      setAvailableServices(resp2)
      setLoading(false)
    }

    void getData()
  }, [])

  const handleContinue = (): void => {
    if (selectedService === null) {
      toast.error('Please select a Service.')
    } else if (selectedChildID === null) {
      toast.error('Please select a Child.')
    } else {
      props.dataSelected(selectedService, selectedChildID)
    }
  }

  if (loading) {
    return <CircularProgress />
  } else if (availableServices.length === 0) {
    return <Typography variant='h6' component='div' data-testid='no-services' >
      This ISD does not currently have any services available.Please contact your ISD to request services.
    </Typography >
  } else if (children.length === 0) {
    return <Typography variant='h6' component='div' data-testid='no-children' >
      You do not have any children associated with your account. Please add a child to continue.
    </Typography >
  } else {
    return <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant='h5' sx={{ mb: '30px' }} component='div' data-testid="service-selection-prompt">
          <CustomLabelWithToolTip
            name='IF_ServiceFinder_Intro'
            defaultLabelText='Here are some services and resources that may be available to you and your children.'
            isdId={props.isdID}
          />
        </Typography>
        <Typography variant='subtitle1' sx={{ mb: '30px' }} component='div'>
          <CustomLabelWithToolTip
            name='IF_ServiceFinder_Subtext'
            defaultLabelText='If you need multiple services or want to include multiple children, you can add them in a separte request. Select a service and child below to get started.'
            isdId={props.isdID}
          />
        </Typography>
      </Grid>

      <Grid item xs={12}>
        {availableServices.map(service =>
          <Box key={service.id}>
            <LargeCard
              fullWidth={true}
              data-testid={'service-card-' + service.id.toString()}
              className={'services-option-card' + (selectedService?.id === service.id ? ' selected' : '')}
              onClick={() => { setSelectedService(service); setSelectedChildID(null) }}
              content={
                <Box className='d-flex'>
                  <Box sx={{ marginLeft: '10px', marginRight: '25px' }}>
                    <Typography variant="h5" className='card-subheader' >{service.name}</Typography>
                    <Box sx={{ marginTop: '10px' }}>{service.description}</Box>
                  </Box>
                </Box>
              }
            />
            {selectedService?.id === service.id && <Box className='children-list-container'>
              {children.map(child => {
                return <SelectableRow
                  key={childName(child) + (child.dateOfBirth?.toString() ?? '')}
                  text={`${childName(child)} ${childAge(child)}`}
                  id={child.id}
                  selectedId={selectedChildID}
                  setSelected={setSelectedChildID}
                />
              })}
            </Box>}
          </Box>
        )}

      </Grid>

      <Grid item xs={6} sx={{ mb: '20px', mt: '40px' }}>
        <Button
          name='continueButton'
          className='footer-button'
          data-testid='continue-button'
          onClick={handleContinue}
          variant='contained'
        >
          Continue
        </Button>
      </Grid>
    </Grid>
  }
}
